import { useState, useEffect } from 'react';

export type FilterIntensity = 'low' | 'medium' | 'high';

interface ScreenFiltersState {
  enabled: boolean;
  filters: {
    crt: { enabled: boolean; intensity: FilterIntensity };
    vhs: { enabled: boolean; intensity: FilterIntensity };
    terminal: { enabled: boolean; intensity: FilterIntensity };
    horror: { enabled: boolean; intensity: FilterIntensity };
  };
  layoutEffects: {
    scanlines: boolean;
    scanningBeam: boolean;
    pageTransitions: boolean;
  };
  performanceMode: boolean;
}

const DEFAULT_STATE: ScreenFiltersState = {
  enabled: false,
  filters: {
    crt: { enabled: false, intensity: 'medium' },
    vhs: { enabled: false, intensity: 'medium' },
    terminal: { enabled: false, intensity: 'medium' },
    horror: { enabled: false, intensity: 'medium' },
  },
  layoutEffects: {
    scanlines: false,
    scanningBeam: false,
    pageTransitions: true,
  },
  performanceMode: false,
};

const STORAGE_KEY = 'screen-filters-settings';

export const useScreenFilters = () => {
  const [state, setState] = useState<ScreenFiltersState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsedState = stored ? { ...DEFAULT_STATE, ...JSON.parse(stored) } : DEFAULT_STATE;
      console.log('Initial screen filters state:', parsedState);
      return parsedState;
    } catch (error) {
      console.warn('Failed to load screen filter settings:', error);
      return DEFAULT_STATE;
    }
  });

  // Broadcast state changes via custom event for real-time sync
  const broadcastStateChange = (newState: ScreenFiltersState) => {
    const event = new CustomEvent('screen-filters:update', { 
      detail: newState 
    });
    window.dispatchEvent(event);
  };

  // Persist state changes to localStorage
  useEffect(() => {
    try {
      console.log('Saving to localStorage:', state);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      broadcastStateChange(state);
    } catch (error) {
      console.warn('Failed to save screen filter settings:', error);
    }
  }, [state]);

  // Listen for external state changes (from other instances or storage events)
  useEffect(() => {
    const handleExternalUpdate = (event: CustomEvent<ScreenFiltersState>) => {
      console.log('Received external state update:', event.detail);
      setState(event.detail);
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        try {
          const newState = { ...DEFAULT_STATE, ...JSON.parse(event.newValue) };
          console.log('Storage changed, updating state:', newState);
          setState(newState);
        } catch (error) {
          console.warn('Failed to parse storage update:', error);
        }
      }
    };

    window.addEventListener('screen-filters:update', handleExternalUpdate as EventListener);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('screen-filters:update', handleExternalUpdate as EventListener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const toggleEnabled = () => {
    console.log('toggleEnabled called, current enabled:', state.enabled);
    console.log('Current full state:', state);
    setState(prev => {
      const newState = { 
        ...prev, 
        enabled: !prev.enabled,
        // When disabling master toggle, also disable all individual filters
        filters: !prev.enabled ? prev.filters : {
          crt: { ...prev.filters.crt, enabled: false },
          vhs: { ...prev.filters.vhs, enabled: false },
          terminal: { ...prev.filters.terminal, enabled: false },
          horror: { ...prev.filters.horror, enabled: false },
        }
      };
      console.log('New state after toggle:', newState);
      return newState;
    });
  };

  const toggleFilter = (filterType: keyof ScreenFiltersState['filters']) => {
    console.log('toggleFilter called with:', filterType);
    console.log('Current filter state:', state.filters[filterType]);
    setState(prev => {
      const newFilterEnabled = !prev.filters[filterType].enabled;
      const hasAnyFilterEnabled = Object.values(prev.filters).some((filter, index) => 
        index === Object.keys(prev.filters).indexOf(filterType) ? newFilterEnabled : filter.enabled
      );
      
      const newState = {
        ...prev,
        // Auto-enable master toggle if any filter is enabled
        enabled: hasAnyFilterEnabled || prev.enabled,
        filters: {
          ...prev.filters,
          [filterType]: {
            ...prev.filters[filterType],
            enabled: newFilterEnabled
          }
        }
      };
      console.log('New filter state:', newState.filters[filterType]);
      console.log('Master enabled:', newState.enabled);
      return newState;
    });
  };

  const setFilterIntensity = (filterType: keyof ScreenFiltersState['filters'], intensity: FilterIntensity) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [filterType]: {
          ...prev.filters[filterType],
          intensity
        }
      }
    }));
  };

  const toggleLayoutEffect = (effectType: keyof ScreenFiltersState['layoutEffects']) => {
    setState(prev => ({
      ...prev,
      layoutEffects: {
        ...prev.layoutEffects,
        [effectType]: !prev.layoutEffects[effectType]
      }
    }));
  };

  const togglePerformanceMode = () => {
    setState(prev => ({ ...prev, performanceMode: !prev.performanceMode }));
  };

  const resetToDefaults = () => {
    console.log('Resetting to defaults');
    setState({ ...DEFAULT_STATE });
  };

  const getFilterClasses = () => {
    // Master toggle must be enabled, otherwise return empty string
    if (!state.enabled) {
      console.log('getFilterClasses: master toggle disabled');
      return '';
    }

    const activeFilters = Object.entries(state.filters).filter(([_, filter]) => filter.enabled);
    
    // If no filters are active, don't render anything
    if (activeFilters.length === 0) {
      console.log('getFilterClasses: no active filters');
      return '';
    }
    
    const classes = ['screen-filter-layer'];
    
    // Add filter classes - each filter gets applied
    activeFilters.forEach(([filterType, filter]) => {
      classes.push(`filter-${filterType}`);
      classes.push(`${filterType}-intensity-${filter.intensity}`);
    });
    
    if (state.performanceMode) {
      classes.push('performance-mode');
    }
    
    const classString = classes.join(' ');
    console.log('getFilterClasses:', { 
      enabled: state.enabled,
      activeFilters,
      classes, 
      classString
    });
    
    return classString;
  };

  const getActiveFiltersCount = () => {
    return Object.values(state.filters).filter(filter => filter.enabled).length;
  };

  const syncNow = () => {
    console.log('Manual sync triggered');
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      broadcastStateChange(state);
      return true;
    } catch (error) {
      console.warn('Failed to sync settings:', error);
      return false;
    }
  };

  return {
    ...state,
    toggleEnabled,
    toggleFilter,
    setFilterIntensity,
    toggleLayoutEffect,
    togglePerformanceMode,
    resetToDefaults,
    getFilterClasses,
    getActiveFiltersCount,
    syncNow,
  };
};